import React, { Fragment } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { Dialog, Switch, Transition } from "@headlessui/react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import {
  hotToast,
  notificationContent,
} from "@/components/notification-toaster.js";

import { XMarkIcon } from "@heroicons/react/24/solid";

import { classNames } from "@/helpers/classNames";

import MarkdownWrapper from "@/components/markdownWrapper";

function Modal({
  data,
  formVersion = 0,
  open,
  setOpen,
  classNameBackground = "",
  srTextClose = "Close",
}) {
  const query = useStaticQuery(graphql`
    query contactCentered {
      sitePlugin(name: { eq: "gatsby-source-strapi-graphql" }) {
        pluginOptions
      }
    }
  `);

  const collectionApiUrl =
    query?.sitePlugin?.pluginOptions?.apiURL &&
    `${query?.sitePlugin?.pluginOptions?.apiURL}/api/messages`;

  if (!collectionApiUrl || !data) {
    return <></>;
  }

  if (data.version) {
    formVersion = isNaN(Number(data.version))
      ? data.version
      : Number(data.version);
  }

  const fields = {};
  for (let field of data.fields || []) {
    fields[field.name] = field;
  }

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const onError = (errors) => {
    hotToast.remove();
    let missingfield = data.fields.find((field) => errors?.[field.name]);
    if (missingfield?.missingTitle && missingfield?.missingText) {
      hotToast.error(
        notificationContent(missingfield.missingTitle, missingfield.missingText)
      );
    } else {
      hotToast.error(
        notificationContent(
          "Missing fields",
          'Please fill out all Fields marked with "*".'
        )
      );
    }
  };
  const onSubmit = (formData) => {
    let mappedData = mapData(formData);

    var xhr = new XMLHttpRequest();
    xhr.open("POST", collectionApiUrl, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.onreadystatechange = function () {
      if (xhr.readyState === 4) {
        hotToast.remove();
        if (xhr.status === 200) {
          hotToast.success(
            notificationContent(
              data.transmitSuccessTitle || "Message send!",
              data.transmitSuccessText ||
                "Your message was successfully send to us."
            )
          );
          reset();
          setOpen(false);
        } else {
          hotToast.error(
            notificationContent(
              data.transmitErrorTitle || "Error",
              data.transmitErrorText ||
                "We couldn't send your message. Please try again later."
            )
          );
        }
      }
    };
    xhr.send(mappedData);
  };

  const mapData = (d) => {
    return JSON.stringify({
      data: {
        Name: d.name,
        Company: d.company,
        Mail: d.mail,
        Phone: d.phone,
        Message: d.message,
        Newsletter: Boolean(d.newsletter),
        FormId: `contact-form-${formVersion}`,
      },
    });
  };

  const inputFormField = (fieldData, type = "text", options = {}) => {
    return (
      <>
        <label
          htmlFor={`inputField-${fieldData.name}`}
          className="inline text-sm font-medium text-text-primary"
        >
          {fieldData.label}
          {fieldData.required && (
            <span className="ml-1 inline text-[#ff0000]">*</span>
          )}
        </label>
        <div className="mt-1">
          <input
            id={`inputField-${fieldData.name}`}
            type={type}
            {...register(
              fieldData.name,
              Object.assign(options, {
                required: fieldData.required,
              })
            )}
            className={classNames(
              "block w-full rounded-md border-border-gray px-4 py-3 shadow-sm placeholder:text-text-secondary focus:border-border-primary focus:ring-1 focus:ring-ring-primary focus:ring-offset-0",
              errors?.[fieldData.name] && "ring-2 ring-[#ff0000] ring-offset-2"
            )}
            placeholder={fieldData.text}
          />
        </div>
      </>
    );
  };

  const switchFormField = (fieldData) => {
    return (
      <Controller
        name={fieldData.name}
        control={control}
        rules={{ required: fieldData.required }}
        render={({ field: { value = false, onChange, onBlur } }) => (
          <Switch.Group>
            <div className="my-4 flex items-start">
              <Switch
                checked={value}
                value={value}
                onClick={onChange}
                onChange={onChange}
                onBlur={onBlur}
                className={classNames(
                  value ? "bg-background-primary" : "bg-background-light-gray",
                  "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-ring-primary focus:ring-offset-2",
                  errors?.[fieldData.name] &&
                    "ring-2 ring-[#ff0000] ring-offset-2"
                )}
              >
                <span
                  aria-hidden="true"
                  className={classNames(
                    value ? "translate-x-5" : "translate-x-0",
                    "inline-block h-5 w-5 transform rounded-full bg-background-white shadow ring-0 transition duration-200 ease-in-out"
                  )}
                />
              </Switch>
              <Switch.Label className="ml-3 inline">
                <MarkdownWrapper
                  text={fieldData.text}
                  markdownClassName="inline text-base text-text-secondary"
                />
                {fieldData.required && (
                  <span className="ml-1 inline text-[#ff0000]">*</span>
                )}
              </Switch.Label>
            </div>
          </Switch.Group>
        )}
      />
    );
  };

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-[999]" onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className={classNames(
                "fixed inset-0 z-[1000] bg-[a5a5a5] bg-opacity-40 transition-opacity",
                classNameBackground
              )}
            />
          </Transition.Child>

          <div className="fixed inset-0 z-[1001] overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-2 text-center sm:items-center md:px-8 md:py-12">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="transform overflow-hidden rounded-lg bg-white p-6 text-left shadow-xl transition-all sm:w-fit">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-start">
                      {data.topic && (
                        <h3 className="block text-center text-base font-semibold uppercase tracking-wide text-text-primary">
                          <MarkdownWrapper text={data.topic} />
                        </h3>
                      )}
                      {data.title && (
                        <h2 className="text-3xl font-extrabold tracking-tight text-text-primary sm:text-4xl">
                          <MarkdownWrapper text={data.title} />
                        </h2>
                      )}
                      {data.description && (
                        <MarkdownWrapper
                          text={data.description}
                          markdownClassName="mt-3 max-w-2xl mx-auto text-xl text-text-secondary sm:mt-4"
                          replaceParagraph={false}
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      className="-mr-2 -mt-2 rounded-md bg-white p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-ring-primary focus:ring-offset-2"
                      onClick={() => setOpen(false)}
                    >
                      <span className="sr-only">{srTextClose || "Close"}</span>
                      <XMarkIcon className="h-8 w-8" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="mt-4 bg-background-white">
                    <div className="mx-auto max-w-3xl bg-background-white">
                      <form
                        onSubmit={handleSubmit(onSubmit, onError)}
                        className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8"
                      >
                        <div>{fields.name && inputFormField(fields.name)}</div>
                        <div>
                          {fields.company && inputFormField(fields.company)}
                        </div>
                        <div>
                          {fields.mail &&
                            inputFormField(fields.mail, "email", {
                              pattern: /^.*@.*$/i,
                            })}
                        </div>
                        <div>
                          {fields.phone && inputFormField(fields.phone, "tel")}
                        </div>
                        {fields.message && (
                          <div className="sm:col-span-2">
                            <label
                              htmlFor={`inputField-${fields.message.name}`}
                              className="block text-sm font-medium text-text-primary"
                            >
                              {fields.message.label || "Message"}
                              {fields.message.required && (
                                <span className="ml-1 text-[#ff0000]">*</span>
                              )}
                            </label>
                            <div className="mt-1">
                              <textarea
                                id={`inputField-${fields.message.name}`}
                                {...register(
                                  fields.message.name,
                                  Object.assign(
                                    {},
                                    {
                                      required: fields.message.required,
                                    }
                                  )
                                )}
                                rows={4}
                                className={classNames(
                                  "block w-full rounded-md border-border-gray px-4 py-3 shadow-sm focus:border-border-primary focus:ring-1 focus:ring-ring-primary focus:ring-offset-0",
                                  errors?.[fields.message.name] &&
                                    "ring-2 ring-[#ff0000] ring-offset-2"
                                )}
                                placeholder={fields.message.text}
                              />
                            </div>
                          </div>
                        )}
                        <div className="sm:col-span-2">
                          {fields.newsletter &&
                            switchFormField(fields.newsletter)}
                          {fields.policy && switchFormField(fields.policy)}
                        </div>
                        <button
                          type="button"
                          className="inline-flex w-full items-center justify-center rounded-md border border-border-primary bg-background-transparent px-6 py-3 text-base font-medium text-text-primary shadow-sm hover:bg-background-off-white focus:outline-none focus:ring-2 focus:ring-ring-primary focus:ring-offset-2"
                          onClick={() => setOpen(false)}
                        >
                          <span className="">{srTextClose || "Close"}</span>
                        </button>
                        <button
                          type="submit"
                          className="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-background-highlight px-6 py-3 text-base font-medium text-text-on-highlight shadow-sm hover:bg-background-highlight-light focus:outline-none focus:ring-2 focus:ring-ring-highlight focus:ring-offset-2"
                        >
                          {data.submitButtonText || "Submit"}
                        </button>
                      </form>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}

Modal.propTypes = {
  data: PropTypes.object,
};

export default Modal;
